import { apiRequest } from '../lib/httpClient'
import type {
  AnalyzeSchemaResponse,
  CreateFileRecordResponse,
  RequiredDateIssue,
  RowRemovalIssue,
  SchemaColumn,
  SchemaMismatch,
  UploadHistoryPage,
  UploadHistoryRow,
  UploadMode,
} from '../types/fileUpload'
import { ApiRequestError } from '../lib/httpClient'

function pickString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function pickNumber(value: unknown, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function pickArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function formatSchemaDataType(row: Record<string, unknown>) {
  const dataType = pickString(row['dataType'] ?? row['DataType'] ?? row['type'] ?? row['Type'])
  const maxLength = row['maxLength'] ?? row['MaxLength']
  const precision = row['precision'] ?? row['Precision']
  const scale = row['scale'] ?? row['Scale']

  if (!dataType) return ''

  const normalizedDataType = dataType.trim()
  if (maxLength != null && maxLength !== '') {
    return `${normalizedDataType}(${pickNumber(maxLength)})`
  }

  if (precision != null && precision !== '') {
    const formattedPrecision = pickNumber(precision)
    const formattedScale = scale != null && scale !== '' ? pickNumber(scale) : 0
    return `${normalizedDataType}(${formattedPrecision}, ${formattedScale})`
  }

  return normalizedDataType
}

function normalizeSchemaColumn(row: Record<string, unknown>): SchemaColumn {
  const maxLength = row['maxLength'] ?? row['MaxLength']
  const precision = row['precision'] ?? row['Precision']
  const scale = row['scale'] ?? row['Scale']

  return {
    name: pickString(row['name'] ?? row['Name']),
    type: formatSchemaDataType(row),
    rawDataType: pickString(row['dataType'] ?? row['DataType'] ?? row['type'] ?? row['Type']),
    nullable: Boolean(row['isNullable'] ?? row['IsNullable'] ?? row['nullable'] ?? row['Nullable']),
    isPrimaryKey: Boolean(row['isPrimaryKey'] ?? row['IsPrimaryKey']),
    sampleValue: pickString(row['sampleValue'] ?? row['SampleValue']),
    maxLength: maxLength != null && maxLength !== '' ? pickNumber(maxLength) : undefined,
    precision: precision != null && precision !== '' ? pickNumber(precision) : undefined,
    scale: scale != null && scale !== '' ? pickNumber(scale) : undefined,
  }
}

function parseMismatchMessage(message: string): SchemaMismatch {
  const columnMatch = message.match(/column '([^']+)'/i)
  return {
    column: columnMatch?.[1] ?? 'Unknown',
    message,
  }
}

function normalizeSchemaMismatch(row: unknown): SchemaMismatch {
  if (typeof row === 'string') return parseMismatchMessage(row)
  const source = (row || {}) as Record<string, unknown>
  return {
    column: pickString(source['column'] ?? source['Column']) || parseMismatchMessage(pickString(source['message'] ?? source['Message'])).column,
    message: pickString(source['message'] ?? source['Message']) || String(row),
  }
}

function normalizeRequiredDateIssue(row: unknown): RequiredDateIssue {
  const source = (row || {}) as Record<string, unknown>
  return {
    columnName: pickString(source['columnName'] ?? source['ColumnName']),
    displayName: pickString(source['displayName'] ?? source['DisplayName']) || pickString(source['columnName'] ?? source['ColumnName']),
    emptyRowCount: pickNumber(source['emptyRowCount'] ?? source['EmptyRowCount']),
    invalidRowCount: pickNumber(source['invalidRowCount'] ?? source['InvalidRowCount']),
  }
}

function normalizeRowRemovalIssue(row: unknown): RowRemovalIssue {
  const source = (row || {}) as Record<string, unknown>
  const columnName = pickString(source['columnName'] ?? source['ColumnName'])
  return {
    columnName,
    displayName: pickString(source['displayName'] ?? source['DisplayName']) || columnName,
    removedRowCount: pickNumber(source['removedRowCount'] ?? source['RemovedRowCount']),
  }
}

function normalizeHistoryRow(row: Record<string, unknown>): UploadHistoryRow {
  const fileId = pickString(row['fileId'] ?? row['FileId'] ?? row['id'] ?? row['Id'])
  return {
    id: fileId,
    fileId,
    fileName: pickString(row['fileName'] ?? row['FileName']),
    tableName: pickString(row['tableName'] ?? row['TableName']),
    status: pickString(row['status'] ?? row['Status']),
    uploadedAt: pickString(row['createdAt'] ?? row['CreatedAt'] ?? row['updatedAt'] ?? row['UpdatedAt']),
    rowCount: pickNumber(row['totalRows'] ?? row['TotalRows']),
    schema: pickArray<Record<string, unknown>>(row['columns'] ?? row['Columns']).map(normalizeSchemaColumn),
  }
}

export async function fetchUploadHistory(input: {
  clientId?: string
  page: number
  pageSize: number
}): Promise<UploadHistoryPage> {
  const payload = await apiRequest<UploadHistoryPage>('file/ui/history', {
    method: 'GET',
    query: {
      clientId: input.clientId,
      page: input.page,
      pageSize: input.pageSize,
    },
  })
  const raw = payload as unknown as Record<string, unknown>
  const items = pickArray<Record<string, unknown>>(payload.items ?? raw['Items']).map(normalizeHistoryRow)
  return {
    items,
    totalPages: pickNumber(payload.totalPages ?? raw['TotalPages'] ?? raw['totalPages'], 1),
    totalItems: pickNumber(
      payload.totalItems ?? raw['TotalItems'] ?? raw['totalCount'] ?? raw['TotalCount'],
      items.length,
    ),
  }
}

export async function fetchClientTableNames(clientId?: string): Promise<string[]> {
  const payload = await apiRequest<unknown>('file/ui/tables', {
    method: 'GET',
    query: { clientId },
  })
  if (Array.isArray(payload)) return payload.map((item) => String(item))
  const root = (payload || {}) as Record<string, unknown>
  const items = pickArray<unknown>(root['items'] ?? root['Items'])
  return items.map((item) => String(item))
}

export async function analyzeSchema(input: {
  file: File
  tableName: string
  uploadMode: UploadMode
  clientId?: string
}): Promise<AnalyzeSchemaResponse> {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append('tableName', input.tableName)
  formData.append('uploadMode', input.uploadMode)

  try {
    const payload = await apiRequest<AnalyzeSchemaResponse>('file/ui/analyze-schema', {
      method: 'POST',
      query: { clientId: input.clientId },
      formData,
    })
    const raw = payload as unknown as Record<string, unknown>
    const columns = pickArray<Record<string, unknown>>(payload.columns ?? raw['Columns']).map(normalizeSchemaColumn)
    const existingTableColumns = pickArray<Record<string, unknown>>(raw['existingTableColumns'] ?? raw['ExistingTableColumns']).map(normalizeSchemaColumn)
    return {
      fileId: payload.fileId ?? pickString(raw['FileId']),
      columns,
      tableName: payload.tableName ?? pickString(raw['TableName']),
      tableDDL: payload.tableDDL ?? pickString(raw['TableDDL']),
      totalRows: pickNumber(payload.totalRows ?? raw['TotalRows']),
      warnings: pickArray<string>(payload.warnings ?? raw['Warnings']).map(String),
      errors: pickArray<string>(payload.errors ?? raw['Errors']).map(String),
      ruleViolations: pickArray<string>(raw['ruleViolations'] ?? raw['RuleViolations']).map(String),
      hasExistingTable: Boolean(payload.hasExistingTable ?? raw['HasExistingTable']),
      existingTableColumns,
      schemaMismatches: pickArray<unknown>(payload.schemaMismatches ?? raw['SchemaMismatches']).map(normalizeSchemaMismatch),
      requiredDateIssues: pickArray<unknown>(payload.requiredDateIssues ?? raw['RequiredDateIssues']).map(normalizeRequiredDateIssue),
      rowRemovalIssues: pickArray<unknown>(payload.rowRemovalIssues ?? raw['RowRemovalIssues']).map(normalizeRowRemovalIssue),
      originalRowCount: pickNumber(payload.originalRowCount ?? raw['OriginalRowCount']),
      retainedRowCount: pickNumber(payload.retainedRowCount ?? raw['RetainedRowCount']),
      removedRowCount: pickNumber(payload.removedRowCount ?? raw['RemovedRowCount']),
    }
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const payload = (error.payload || {}) as Record<string, unknown>
      const mismatches = pickArray<unknown>(payload['mismatches'] ?? payload['Mismatches'] ?? payload['schemaMismatches'] ?? payload['SchemaMismatches']).map(normalizeSchemaMismatch)
      const requiredDateIssues = pickArray<unknown>(payload['requiredDateIssues'] ?? payload['RequiredDateIssues']).map(normalizeRequiredDateIssue)
      const rowRemovalIssues = pickArray<unknown>(payload['rowRemovalIssues'] ?? payload['RowRemovalIssues']).map(normalizeRowRemovalIssue)

      if (mismatches.length > 0 || requiredDateIssues.length > 0 || rowRemovalIssues.length > 0) {
        throw new ApiRequestError(error.message, error.status, error.code, {
          ...payload,
          mismatches,
          requiredDateIssues,
          rowRemovalIssues,
        })
      }
    }

    throw error
  }
}

export async function createFileRecord(input: {
  file: File
  tableName: string
  uploadMode: UploadMode
  clientId?: string
}): Promise<CreateFileRecordResponse> {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append('tableName', input.tableName)
  formData.append('uploadMode', input.uploadMode)

  try {
    const payload = await apiRequest<CreateFileRecordResponse>('file/ui/create', {
      method: 'POST',
      query: { clientId: input.clientId },
      formData,
    })
    const raw = payload as unknown as Record<string, unknown>
    return {
      fileId: payload.fileId ?? pickString(raw['FileId']),
      columns: pickArray<Record<string, unknown>>(payload.columns ?? raw['Columns']).map(normalizeSchemaColumn),
    }
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const payload = (error.payload || {}) as Record<string, unknown>
      const mismatches = pickArray<unknown>(payload['mismatches'] ?? payload['Mismatches']).map(normalizeSchemaMismatch)
      const requiredDateIssues = pickArray<unknown>(payload['requiredDateIssues'] ?? payload['RequiredDateIssues']).map(normalizeRequiredDateIssue)
      const rowRemovalIssues = pickArray<unknown>(payload['rowRemovalIssues'] ?? payload['RowRemovalIssues']).map(normalizeRowRemovalIssue)

      if (mismatches.length > 0 || requiredDateIssues.length > 0 || rowRemovalIssues.length > 0) {
        throw new ApiRequestError(error.message, error.status, error.code, {
          ...payload,
          mismatches,
          requiredDateIssues,
          rowRemovalIssues,
        })
      }
    }

    throw error
  }
}
